import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Navigation } from 'lucide-react';
import { PROPERTIES } from '../utils/constants';
import ScrollReveal from './ScrollReveal';

const LocationSection = () => {
  return (
    <section id="contact" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <ScrollReveal direction="up">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center mb-6">
              <div className="h-px w-20 bg-gradient-to-r from-transparent to-gold-400" />
              <MapPin className="mx-4 w-6 h-6 text-gold-400" />
              <div className="h-px w-20 bg-gradient-to-l from-transparent to-gold-400" />
            </div>
            
            <h2 className="text-5xl md:text-6xl font-serif text-navy-900 mb-6">
              Find Us in Bhopal
            </h2>
            
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
              Three strategic locations across Bhopal, each offering easy accessibility 
              and excellent connectivity.
            </p>
          </div>
        </ScrollReveal>

        {/* Properties Location Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-16 max-w-7xl mx-auto">
          {PROPERTIES.map((property, index) => (
            <ScrollReveal key={index} direction="up" delay={index * 0.15}>
              <motion.div
                whileHover={{ y: -10 }}
                className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-lg p-8 hover:shadow-2xl transition-all duration-500 group"
              >
                {/* Property Name */}
                <div className="mb-6">
                  <h3 className="text-2xl font-serif text-navy-900 mb-2 group-hover:text-gold-500 transition-colors duration-300">
                    {property.name}
                  </h3>
                  <p className="text-sm text-gold-500 font-semibold">{property.location}</p>
                </div>

                {/* Divider */}
                <div className="h-px bg-gradient-to-r from-gold-400 to-transparent mb-6" />

                {/* Address */}
                <div className="space-y-4 mb-6">
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-gold-500 flex-shrink-0 mt-1" />
                    <p className="text-gray-600 text-sm leading-relaxed">{property.address}</p>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Phone className="w-5 h-5 text-gold-500 flex-shrink-0" />
                    <a 
                      href={`tel:${property.phone}`}
                      className="text-gray-600 hover:text-gold-500 transition-colors duration-300 text-sm"
                    >
                      {property.phone}
                    </a>
                  </div>
                </div>

                {/* Get Directions Button */}
                <motion.button 
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full flex items-center justify-center space-x-2 px-6 py-3 border-2 border-gold-400 text-gold-600 hover:bg-gold-400 hover:text-navy-900 transition-all duration-300 font-semibold tracking-wider rounded"
                >
                  <Navigation className="w-4 h-4" />
                  <span>GET DIRECTIONS</span>
                </motion.button>
              </motion.div>
            </ScrollReveal>
          ))}
        </div>

        {/* Main Map Section */}
        <ScrollReveal direction="up" delay={0.3}>
          <div className="max-w-7xl mx-auto">
            <div className="bg-gray-100 rounded-2xl overflow-hidden shadow-2xl">
              {/* Map Embed - Jalsa Retreat (Flagship) */}
              <div className="relative h-[500px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3666.4744791877973!2d77.39874!3d23.188899!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x397c43b0dcf6ad33%3A0x6c8e93be0c8c9d5f!2sJalsa%20Retreat!5e0!3m2!1sen!2sin!4v1699999999999!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Jalsa Group of Hotels Location"
                />
              </div>

              {/* Map Footer Info */}
              <div className="bg-gradient-to-r from-navy-900 to-navy-800 p-8">
                <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
                  <div className="text-center md:text-left">
                    <h3 className="text-2xl font-serif text-white mb-2">
                      Need Help Finding Us?
                    </h3>
                    <p className="text-gray-300">
                      Our team is here to assist you with directions and travel arrangements
                    </p>
                  </div>
                  
                  <div className="flex flex-wrap gap-4">
                    <motion.a
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      href="tel:+917554229796"
                      className="px-8 py-3 bg-gold-400 text-navy-900 hover:bg-white transition-all duration-300 font-semibold tracking-wider rounded shadow-lg"
                    >
                      CALL US
                    </motion.a>
                    <motion.a
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      href="mailto:info@jalsahotels.com"
                      className="px-8 py-3 border-2 border-gold-400 text-gold-400 hover:bg-gold-400 hover:text-navy-900 transition-all duration-300 font-semibold tracking-wider rounded"
                    >
                      EMAIL US
                    </motion.a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ScrollReveal>

        {/* Contact Info Grid */}
        <ScrollReveal direction="up" delay={0.4}>
          <div className="grid md:grid-cols-3 gap-8 mt-16 max-w-5xl mx-auto">
            {[
              { icon: Phone, title: "Call Us", value: "+91 755 422 9796", href: "tel:+917554229796" },
              { icon: Mail, title: "Email Us", value: "info@jalsahotels.com", href: "mailto:info@jalsahotels.com" },
              { icon: MapPin, title: "Visit Us", value: "Bhopal, Madhya Pradesh", href: null }
            ].map((contact, index) => (
              <motion.div 
                key={index}
                className="text-center"
                whileHover={{ scale: 1.05 }}
              >
                <div className="w-16 h-16 bg-gold-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <contact.icon className="w-8 h-8 text-navy-900" />
                </div>
                <h4 className="text-lg font-semibold text-navy-900 mb-2">{contact.title}</h4>
                {contact.href ? (
                  <a href={contact.href} className="text-gold-500 hover:text-gold-600 transition-colors duration-300">
                    {contact.value}
                  </a>
                ) : (
                  <p className="text-gray-600">{contact.value}</p>
                )}
              </motion.div>
            ))}
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default LocationSection;
